import { supabase } from '@/integrations/supabase/client';

export interface StarterCard {
  title: string;
  text: string;
  position: { x: number; y: number };
}

export type GoalPhase = 'exploring' | 'planned' | 'executing';

/**
 * Generates phase-appropriate starter cards for a goal board.
 * Uses Gemini via the partnr-chat edge function pattern for AI generation,
 * with a deterministic fallback if the API call fails.
 */
export async function generateStarterCards(
  goal: { text: string; phase: GoalPhase },
  userRole: string
): Promise<StarterCard[]> {
  // Position cards in a spaced-out cluster (no overlap)
  const positions = [
    { x: 120 + Math.random() * 30, y: 120 + Math.random() * 20 },
    { x: 420 + Math.random() * 30, y: 100 + Math.random() * 20 },
    { x: 260 + Math.random() * 30, y: 350 + Math.random() * 20 },
  ];

  try {
    const session = await supabase.auth.getSession();
    if (!session.data.session?.access_token) {
      return getFallbackCards(goal, positions);
    }

    const systemPrompt = `You are Partnr, an AI assistant inside Mindfull — a visual thinking workspace.
You're generating starter cards for a new board.

The user is a ${userRole}.
Their goal: "${goal.text}"
Their current phase: ${goal.phase}

Generate exactly 3 starter cards. Each card should be a short, actionable prompt
that helps the user make progress on their goal given their current phase.

Phase guidelines:
- "exploring": Research-oriented. Questions to answer, references to collect, frameworks to consider.
- "planned": Structure-oriented. Plan components, milestones, key decisions to make.
- "executing": Action-oriented. Next steps, tracking items, things to unblock.

Voice rules:
- Be direct and specific to their goal. No generic advice.
- Keep each card under 20 words.
- Don't use exclamation marks.
- Don't be motivational — be useful.
- Write like a sharp strategist, not a chatbot.

Return ONLY a JSON array: [{"title": "short title", "text": "card text"}, {"title": "short title", "text": "card text"}, {"title": "short title", "text": "card text"}]
Each title should be 2-4 words that categorize the card.`;

    // Abort after 8 seconds to prevent hanging on slow/unavailable edge functions
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partnr-chat`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.data.session.access_token}`,
        },
        body: JSON.stringify({
          messages: [
            { role: 'user', content: `Generate starter cards for my goal: "${goal.text}"` },
          ],
        }),
        signal: controller.signal,
      }
    );

    if (!response.ok || !response.body) {
      clearTimeout(timeoutId);
      return getFallbackCards(goal, positions);
    }

    // Read the full streamed response
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullText = '';
    let textBuffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      textBuffer += decoder.decode(value, { stream: true });

      let newlineIndex: number;
      while ((newlineIndex = textBuffer.indexOf('\n')) !== -1) {
        let line = textBuffer.slice(0, newlineIndex);
        textBuffer = textBuffer.slice(newlineIndex + 1);

        if (line.endsWith('\r')) line = line.slice(0, -1);
        if (line.startsWith(':') || line.trim() === '') continue;
        if (!line.startsWith('data: ')) continue;

        const jsonStr = line.slice(6).trim();
        if (jsonStr === '[DONE]') break;

        try {
          const parsed = JSON.parse(jsonStr);
          const content = parsed.choices?.[0]?.delta?.content;
          if (content) fullText += content;
        } catch {
          // Skip malformed chunks
        }
      }
    }

    clearTimeout(timeoutId);

    // Try to extract JSON array from the response
    const jsonMatch = fullText.match(/\[[\s\S]*?\]/);
    if (jsonMatch) {
      const cards = JSON.parse(jsonMatch[0]) as Array<{ title?: string; text: string }>;
      if (Array.isArray(cards) && cards.length >= 3) {
        return cards.slice(0, 3).map((card, i) => ({
          title: card.title || '',
          text: card.text,
          position: positions[i],
        }));
      }
    }

    return getFallbackCards(goal, positions);
  } catch {
    return getFallbackCards(goal, positions);
  }
}

/**
 * Deterministic fallback cards when AI is unavailable.
 */
function getFallbackCards(
  goal: { text: string; phase: GoalPhase },
  positions: Array<{ x: number; y: number }>
): StarterCard[] {
  const phaseCards: Record<GoalPhase, Array<{ title: string; text: string }>> = {
    exploring: [
      { title: 'Define Success', text: `What does success look like for "${truncate(goal.text, 30)}"?` },
      { title: 'Key Resources', text: 'List 3 people or resources to learn from' },
      { title: 'Open Questions', text: 'What are the biggest unknowns right now?' },
    ],
    planned: [
      { title: 'First Milestone', text: 'Define the first milestone to hit' },
      { title: 'Key Decisions', text: 'What decisions need to be made before starting?' },
      { title: 'Dependencies', text: 'Map out the key dependencies' },
    ],
    executing: [
      { title: 'Next Action', text: 'What is the single next action to take?' },
      { title: 'Blockers', text: 'What is currently blocking progress?' },
      { title: 'Progress Log', text: 'Track: what has been completed so far' },
    ],
  };

  const cards = phaseCards[goal.phase];
  return cards.map((card, i) => ({
    title: card.title,
    text: card.text,
    position: positions[i],
  }));
}

function truncate(text: string, maxLen: number): string {
  return text.length > maxLen ? text.slice(0, maxLen) + '...' : text;
}
